import type { SourceDto, SourceRef } from '../types';

// Phase-A fixture source catalog + the reusable SourceRef builders the company fixtures
// cite. The catalog rows are the REAL registries/feeds the Phase-B adapters will poll
// (docs/plans/ma-tracker.md B1); the per-fact SourceRef deep links below point at
// FICTIONAL documents on those publishers — placeholder provenance replaced by the real
// agent-authored seed in A6.

export const SOURCES: SourceDto[] = [
  {
    slug: 'sec-edgar',
    name: 'SEC EDGAR',
    url: 'https://www.sec.gov/cgi-bin/srqsb?text=form-type%3D8-K',
    type: 'government',
    access: 'free',
    jurisdiction: 'US',
    notes: 'Full-text search of 8-K items 1.03/2.05/2.06 + SC 13D; annual reports for listed companies.',
  },
  {
    slug: 'courtlistener',
    name: 'CourtListener (RECAP)',
    url: 'https://www.courtlistener.com/',
    type: 'court',
    access: 'registration',
    jurisdiction: 'US',
    notes: 'Bankruptcy dockets (txsb / deb / nysb wave 1). Search is anonymous; docket detail needs the token.',
  },
  {
    slug: 'companies-house',
    name: 'Companies House',
    url: 'https://find-and-update.company-information.service.gov.uk/',
    type: 'registry',
    access: 'free',
    jurisdiction: 'UK',
    notes: 'Company profile, officers, insolvency status and free iXBRL private-company accounts.',
  },
  {
    slug: 'the-gazette',
    name: 'The Gazette (insolvency notices)',
    url: 'https://www.thegazette.co.uk/insolvency',
    type: 'government',
    access: 'free',
    jurisdiction: 'UK',
    notes: 'Statutory insolvency notices — Atom feed + per-notice JSON-LD, filtered by corporate notice codes.',
  },
  {
    slug: 'brreg',
    name: 'Brønnøysundregistrene',
    url: 'https://www.brreg.no/',
    type: 'registry',
    access: 'free',
    jurisdiction: 'NO',
    notes: 'Enhetsregisteret company records incl. the konkurs flag; no auth.',
  },
  {
    slug: 'regnskapsregisteret',
    name: 'Regnskapsregisteret',
    url: 'https://data.brreg.no/regnskapsregisteret/regnskap/',
    type: 'registry',
    access: 'free',
    jurisdiction: 'NO',
    notes: 'Full annual accounts as JSON per orgnr — confirmed-grade financials, free.',
  },
  {
    slug: 'bundesanzeiger',
    name: 'Bundesanzeiger / Unternehmensregister',
    url: 'https://www.unternehmensregister.de/',
    type: 'registry',
    access: 'free',
    jurisdiction: 'DE',
    notes: 'German company filings and annual accounts (HRB records).',
  },
  {
    slug: 'acra',
    name: 'ACRA BizFile',
    url: 'https://www.bizfile.gov.sg/',
    type: 'registry',
    access: 'registration',
    jurisdiction: 'SG',
    notes: 'Singapore company register; paid extracts for full profiles.',
  },
  {
    slug: 'fitch-ratings',
    name: 'Fitch Ratings',
    url: 'https://www.fitchratings.com/',
    type: 'ratings_agency',
    access: 'registration',
    jurisdiction: null,
    notes: 'Rating-action RSS verified live; downgrades land as credit_downgrade events (headline-level).',
  },
  {
    slug: 'offshore-engineer',
    name: 'Offshore Engineer',
    url: 'https://www.oedigital.com/',
    type: 'media',
    access: 'free',
    jurisdiction: null,
    notes: 'Trade press — sale-process and contract reporting; reported confidence, always linked.',
  },
  {
    slug: 'upstream-online',
    name: 'Upstream',
    url: 'https://www.upstreamonline.com/',
    type: 'media',
    access: 'paywalled',
    jurisdiction: null,
    notes: 'Paywalled trade press. Catalog entry only — never scraped.',
  },
];

/* ---------- SourceRef builders (fixture provenance for the fictional companies) ---------- */

export function brregRef(orgnr: string, title: string, date: string): SourceRef {
  return {
    sourceName: 'Brønnøysundregistrene',
    sourceType: 'registry',
    documentTitle: title,
    url: `https://virksomhet.brreg.no/nb/oppslag/enheter/${orgnr}`,
    documentDate: date,
  };
}

export function regnskapRef(orgnr: string, fy: number): SourceRef {
  return {
    sourceName: 'Regnskapsregisteret',
    sourceType: 'registry',
    documentTitle: `Annual accounts ${fy} (årsregnskap)`,
    url: `https://data.brreg.no/regnskapsregisteret/regnskap/${orgnr}`,
    documentDate: `${fy + 1}-06-30`,
  };
}

export function companiesHouseRef(crn: string, title: string, date: string): SourceRef {
  return {
    sourceName: 'Companies House',
    sourceType: 'registry',
    documentTitle: title,
    url: `https://find-and-update.company-information.service.gov.uk/company/${crn}`,
    documentDate: date,
  };
}

export function edgarRef(cik: string, title: string, date: string): SourceRef {
  return {
    sourceName: 'SEC EDGAR',
    sourceType: 'government',
    documentTitle: title,
    url: `https://www.sec.gov/cgi-bin/browse-edgar?action=getcompany&CIK=${cik}`,
    documentDate: date,
  };
}

export function courtRef(caseNo: string, title: string, date: string): SourceRef {
  return {
    sourceName: 'CourtListener',
    sourceType: 'court',
    documentTitle: title,
    url: `https://www.courtlistener.com/docket/${caseNo.replace(/[^0-9]/g, '')}/fixture/`,
    documentDate: date,
    pageRef: caseNo,
  };
}

export function unternehmensregisterRef(hrb: string, title: string, date: string): SourceRef {
  return {
    sourceName: 'Unternehmensregister',
    sourceType: 'registry',
    documentTitle: title,
    url: `https://www.unternehmensregister.de/ureg/result.html?submitaction=showDocument&id=${hrb}`,
    documentDate: date,
  };
}

export function acraRef(uen: string, title: string, date: string): SourceRef {
  return {
    sourceName: 'ACRA BizFile',
    sourceType: 'registry',
    documentTitle: title,
    url: `https://www.bizfile.gov.sg/ngbbizfileinternet/faces/oracle/webcenter/portalapp/pages/BizfileHomepage.jspx?uen=${uen}`,
    documentDate: date,
  };
}

export function companyRef(host: string, title: string, date: string, path = ''): SourceRef {
  return {
    sourceName: host,
    sourceType: 'company',
    documentTitle: title,
    url: `https://www.${host}/${path}`,
    documentDate: date,
  };
}

export function mediaRef(outlet: 'Offshore Engineer' | 'Upstream', title: string, date: string, slugPath: string): SourceRef {
  const host = outlet === 'Offshore Engineer' ? 'www.oedigital.com' : 'www.upstreamonline.com';
  return {
    sourceName: outlet,
    sourceType: 'media',
    documentTitle: title,
    url: `https://${host}/news/${slugPath}`,
    documentDate: date,
  };
}

export function fitchRef(title: string, date: string): SourceRef {
  return {
    sourceName: 'Fitch Ratings',
    sourceType: 'ratings_agency',
    documentTitle: title,
    url: `https://www.fitchratings.com/research/${date}-fixture`,
    documentDate: date,
  };
}
